import React, { useState, useMemo, useEffect } from 'react';
import s from './LayoutModal.module.css';
import { METRICS, CHART_TYPES, COL_OPTIONS, LAYOUT_TYPES, BENTO_TEMPLATES } from '../../data/catalog';
import { useDashboard, A } from '../../context/DashboardContext';
import ConsolidatedConfig, { validateConsolidatedConfig } from './ConsolidatedConfig';
import { isConsolidatedMetric } from '../../utils/consolidatedTable';

/* ─── Chart mini icons ──────────────────────────────── */
function ChartIcon({ type, active }) {
  const c = active ? '#5046e4' : '#9f9f98';
  const l = active ? '#c7d2fe' : '#e8e8e4';
  const icons = {
    kpi: <svg width="42" height="26" viewBox="0 0 42 26"><rect x="2" y="6" width="38" height="9" rx="3" fill={l} /><text x="21" y="14" textAnchor="middle" fontSize="7" fill={c} fontWeight="700" fontFamily="sans-serif">84,230</text><rect x="7" y="17" width="11" height="4" rx="2" fill={c} opacity=".4" /></svg>,
    bar: <svg width="42" height="26" viewBox="0 0 42 26"><rect x="2" y="12" width="7" height="12" rx="2" fill={l} /><rect x="12" y="6" width="7" height="18" rx="2" fill={c} /><rect x="22" y="10" width="7" height="14" rx="2" fill={l} /><rect x="32" y="2" width="7" height="22" rx="2" fill={c} opacity=".7" /></svg>,
    line: <svg width="42" height="26" viewBox="0 0 42 26"><polyline points="2,22 12,16 21,19 31,8 40,12" stroke={c} strokeWidth="2.5" fill="none" strokeLinejoin="round" /><circle cx="40" cy="12" r="2.5" fill={c} /></svg>,
    area: <svg width="42" height="26" viewBox="0 0 42 26"><path d="M2,22 L12,16 L21,19 L31,8 L40,12 L40,24 L2,24Z" fill={l} /><polyline points="2,22 12,16 21,19 31,8 40,12" stroke={c} strokeWidth="2" fill="none" /></svg>,
    ring: <svg width="42" height="26" viewBox="0 0 42 26"><circle cx="21" cy="13" r="10" fill="none" stroke={l} strokeWidth="6" /><circle cx="21" cy="13" r="10" fill="none" stroke={c} strokeWidth="6" strokeDasharray="40 23" transform="rotate(-90 21 13)" /></svg>,
    list: <svg width="42" height="26" viewBox="0 0 42 26"><rect x="2" y="3" width="38" height="5" rx="2" fill={l} /><rect x="2" y="11" width="28" height="5" rx="2" fill={l} /><rect x="2" y="19" width="32" height="5" rx="2" fill={l} /></svg>,
    bar_h: <svg width="42" height="26" viewBox="0 0 42 26"><rect x="2" y="3" width="28" height="5" rx="2" fill={c} /><rect x="2" y="11" width="18" height="5" rx="2" fill={l} /><rect x="2" y="19" width="24" height="5" rx="2" fill={l} /></svg>,
    scatter: <svg width="42" height="26" viewBox="0 0 42 26"><circle cx="10" cy="18" r="3" fill={c} opacity=".7" /><circle cx="18" cy="10" r="2.5" fill={c} /><circle cx="26" cy="14" r="3" fill={l} /><circle cx="34" cy="6" r="2" fill={c} opacity=".6" /><circle cx="8" cy="8" r="2" fill={l} /></svg>,
    composed: <svg width="42" height="26" viewBox="0 0 42 26"><rect x="2" y="12" width="7" height="12" rx="2" fill={l} /><rect x="12" y="8" width="7" height="16" rx="2" fill={l} /><rect x="22" y="10" width="7" height="14" rx="2" fill={l} /><rect x="32" y="4" width="7" height="20" rx="2" fill={l} /><polyline points="5,16 16,10 26,12 36,6" stroke={c} strokeWidth="2.5" fill="none" strokeLinejoin="round" /></svg>,
    stat_delta: <svg width="42" height="26" viewBox="0 0 42 26"><rect x="2" y="2" width="38" height="14" rx="3" fill={l} /><text x="21" y="13" textAnchor="middle" fontSize="7" fill={c} fontWeight="700" fontFamily="sans-serif">$84k</text><rect x="2" y="18" width="16" height="6" rx="2" fill={c} opacity=".3" /><rect x="22" y="18" width="18" height="6" rx="2" fill={c} opacity=".6" /></svg>,
    heatmap: <svg width="42" height="26" viewBox="0 0 42 26"><rect x="2" y="2" width="8" height="8" rx="1" fill={c} opacity=".9" /><rect x="13" y="2" width="8" height="8" rx="1" fill={c} opacity=".4" /><rect x="24" y="2" width="8" height="8" rx="1" fill={c} opacity=".7" /><rect x="35" y="2" width="5" height="8" rx="1" fill={c} opacity=".3" /><rect x="2" y="13" width="8" height="8" rx="1" fill={c} opacity=".3" /><rect x="13" y="13" width="8" height="8" rx="1" fill={c} opacity=".8" /><rect x="24" y="13" width="8" height="8" rx="1" fill={c} opacity=".5" /><rect x="35" y="13" width="5" height="8" rx="1" fill={c} opacity=".9" /></svg>,
    table: <svg width="42" height="26" viewBox="0 0 42 26"><rect x="2" y="2" width="38" height="6" rx="2" fill={c} opacity=".8" /><rect x="2" y="10" width="38" height="4" rx="1" fill={l} /><rect x="2" y="16" width="38" height="4" rx="1" fill={l} /><rect x="2" y="22" width="38" height="3" rx="1" fill={c} opacity=".4" /></svg>,
    funnel: <svg width="42" height="26" viewBox="0 0 42 26"><rect x="4" y="2" width="34" height="5" rx="2" fill={c} /><rect x="8" y="9" width="26" height="5" rx="2" fill={c} opacity=".7" /><rect x="13" y="16" width="16" height="5" rx="2" fill={c} opacity=".5" /><rect x="17" y="22" width="8" height="3" rx="1" fill={c} opacity=".3" /></svg>,
  };
  return icons[type] || null;
}

/* ─── Layout type icons ─────────────────────────────── */
function LayoutTypeIcon({ type, active }) {
  const c = active ? '#5046e4' : '#9f9f98';
  const l = active ? '#c7d2fe' : '#e8e8e4';
  if (type === 'columns') return <svg width="64" height="40" viewBox="0 0 64 40"><rect x="3" y="3" width="18" height="34" rx="4" fill={l} /><rect x="25" y="3" width="18" height="34" rx="4" fill={l} /><rect x="47" y="3" width="14" height="34" rx="4" fill={c} opacity=".6" /></svg>;
  return <svg width="64" height="40" viewBox="0 0 64 40"><rect x="3" y="3" width="28" height="20" rx="3" fill={c} opacity=".7" /><rect x="35" y="3" width="26" height="9" rx="3" fill={l} /><rect x="35" y="15" width="12" height="8" rx="3" fill={l} /><rect x="50" y="15" width="11" height="8" rx="3" fill={l} /><rect x="3" y="27" width="58" height="10" rx="3" fill={l} /></svg>;
}

/* ─── Search box ────────────────────────────────────── */
function SearchBox({ value, onChange, placeholder }) {
  return (
    <div className={s.searchBox}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
      <input className={s.searchInput} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
      {value && <button onClick={() => onChange('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9f9f98', fontSize: 14, lineHeight: 1, padding: 0, flexShrink: 0 }}>✕</button>}
    </div>
  );
}

/* ─── Responsive hook ───────────────────────────────── */
function useBreakpoint() {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  useEffect(() => {
    const handle = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return {
    isMobile: width < 480,   // 1 col list
    isTablet: width < 720,   // 2 cols
    isDesktop: width >= 720,
  };
}

/* ─── Step Metric — with already-added badges ───────── */
function StepMetric({ metricId, onChange, usedMetricIds = [] }) {
  const [search, setSearch] = useState('');
  const [dupTarget, setDupTarget] = useState(null);
  const { isMobile, isTablet } = useBreakpoint();   // ← responsive

  const filtered = useMemo(() =>
    METRICS.filter(m => m.label.toLowerCase().includes(search.toLowerCase())),
    [search]
  );

  const handleSelect = (id) => {
    if (usedMetricIds.includes(id) && id !== metricId) {
      setDupTarget(id);
    } else {
      onChange(id);
    }
  };

  const confirmDup = () => { onChange(dupTarget); setDupTarget(null); };
  const m = METRICS.find(x => x.id === dupTarget);

  /* ── grid override por breakpoint ─────────────────────
     desktop  → CSS module (2–3 cols, tal como está)
     tablet   → 2 columnas forzadas
     mobile   → 1 columna lista (sin corte)
  ──────────────────────────────────────────────────── */
  const gridStyle = isMobile
    ? { display: 'flex', flexDirection: 'column', gap: 6 }
    : isTablet
      ? { gridTemplateColumns: '1fr 1fr' }
      : undefined;

  /* En móvil cada item se ve como fila de lista */
  const itemStyle = isMobile
    ? {
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 12px', borderRadius: 10,
      justifyContent: 'flex-start', textAlign: 'left',
      minHeight: 44,
    }
    : undefined;

  return (
    <div style={{ position: 'relative' }}>
      <p style={{ fontSize: 13, color: '#6b6b66', lineHeight: 1.5, marginBottom: 12 }}>
        Elige la variable que quieres visualizar.
      </p>
      <SearchBox value={search} onChange={setSearch} placeholder="Buscar métrica..." />
      <div className={s.secTitle}>
        Métricas disponibles{' '}
        <span style={{ fontWeight: 400 }}>{filtered.length} de {METRICS.length}</span>
      </div>

      {/* grid: CSS module en desktop, override en tablet/mobile */}
      <div className={s.metricGrid} style={gridStyle}>
        {filtered.map(metric => {
          const isUsed = usedMetricIds.includes(metric.id);
          const isSel = metricId === metric.id;
          return (
            <div
              key={metric.id}
              className={`${s.mOpt} ${isSel ? s.mOptSel : ''}`}
              style={itemStyle}
              onClick={() => handleSelect(metric.id)}
            >
              <div className={s.mDot} style={{ background: metric.color, flexShrink: 0 }} />
              <span className={s.mName} style={{ flex: 1 }}>{metric.label}</span>
              {isUsed && !isSel && (
                <span className={s.mBadge}>✓</span>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className={s.noResults}>Sin resultados</div>
        )}
      </div>

      {/* Duplicate confirmation overlay */}
      {dupTarget && (
        <div className={s.dupConfirm}>
          <div className={s.dupIcon}>⚠️</div>
          <div className={s.dupTitle}>Ya está en el dashboard</div>
          <div className={s.dupDesc}>
            <strong>{m?.label}</strong> ya fue agregada a otro espacio. ¿Quieres usarla de nuevo igualmente?
          </div>
          <div className={s.dupBtns}>
            <button
              onClick={() => setDupTarget(null)}
              style={{ padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer', background: '#fff', border: '1px solid rgba(0,0,0,0.14)', fontFamily: 'inherit' }}
            >
              Cancelar
            </button>
            <button
              onClick={confirmDup}
              style={{ padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer', background: '#5046e4', color: '#fff', border: 'none', fontFamily: 'inherit' }}
            >
              Sí, usar igual
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Step Chart ─────────────────────────────────────── */
function StepChart({ chartType, onChange }) {
  const [search, setSearch] = useState('');
  const { isMobile } = useBreakpoint();   // ← responsive

  // The table belongs to the consolidated sales metric only and is chosen with its own settings, not from this list
  const selectableCharts = useMemo(() => CHART_TYPES.filter(ct => ct.id !== 'table'), []);
  const filtered = useMemo(() =>
    selectableCharts.filter(ct =>
      ct.label.toLowerCase().includes(search.toLowerCase()) ||
      ct.desc.toLowerCase().includes(search.toLowerCase())
    ),
    [search, selectableCharts]
  );

  /* En móvil: 2 columnas compactas (las cards son pequeñas con icono+label) */
  const chartGridStyle = isMobile
    ? { gridTemplateColumns: '1fr 1fr', gap: 8 }
    : undefined;

  return (
    <>
      <p style={{ fontSize: 13, color: '#6b6b66', lineHeight: 1.5, marginBottom: 12 }}>
        ¿Cómo quieres visualizar esta métrica?
      </p>
      <SearchBox value={search} onChange={setSearch} placeholder="Buscar tipo..." />
      <div className={s.secTitle}>
        Tipos de visualización
        <span style={{ fontWeight: 400 }}>{filtered.length} de {selectableCharts.length}</span>
      </div>
      <div className={s.chartGrid} style={chartGridStyle}>
        {filtered.map(ct => {
          const active = chartType === ct.id;
          return (
            <div key={ct.id} className={`${s.cOpt} ${active ? s.cOptSel : ''}`} onClick={() => onChange(ct.id)}>
              <ChartIcon type={ct.id} active={active} />
              <div className={s.cLabel}>{ct.label}</div>
              <div className={s.cDesc}>{ct.desc}</div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className={s.noResults} style={{ gridColumn: '1/-1' }}>Sin resultados</div>
        )}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════
   MAIN MODAL
   Modes:
     'new'         → 2 steps: type → structure
     'editOneSlot' → 2 steps: metric → chart
═══════════════════════════════════════════════════════ */
export default function LayoutModal({
  onClose,
  editLayoutId = null,
  editSlotIdx = null,
  onToast,
}) {
  const { state, dispatch } = useDashboard();
  const existing = editLayoutId ? state.layouts.find(l => l.id === editLayoutId) : null;
  const existingSlot = existing?.slots?.[editSlotIdx] ?? null;
  const mode = !editLayoutId ? 'new' : 'editOneSlot';

  /* ── Compute all metrics already used across all layouts ── */
  const usedMetricIds = useMemo(() =>
    state.layouts.flatMap(l => l.slots.filter(Boolean).map(sl => sl.metricId)),
    [state.layouts]
  );

  /* ── New layout state ── */
  const [step, setStep] = useState(0);
  const [layoutType, setLayoutType] = useState('columns');
  const [cols, setCols] = useState(2);
  const [templateId, setTemplateId] = useState(BENTO_TEMPLATES[0].id);
  const [done, setDone] = useState(false);

  /* ── Slot edit state ── */
  const [slotStep, setSlotStep] = useState(0);
  const [metricId, setMetricId] = useState(existingSlot?.metricId || '');
  const [chartType, setChartType] = useState(existingSlot?.chartType || '');
  // Settings of the consolidated sales table (only used when that metric is chosen)
  const [consolidated, setConsolidated] = useState({
    rows: existingSlot?.config?.rows || 'date',
    group_by: existingSlot?.config?.group_by || 'day',
    cumulative: Boolean(existingSlot?.config?.cumulative),
    series: existingSlot?.config?.series || [],
  });
  const isConsolidated = isConsolidatedMetric(metricId);

  const isBento = layoutType === 'bento';
  const template = BENTO_TEMPLATES.find(t => t.id === templateId) || BENTO_TEMPLATES[0];
  const slotCount = isBento ? template.slots.length : cols;

  const selMetric = METRICS.find(m => m.id === metricId);

  const progressPct = done ? 100
    : mode === 'new'
      ? (step === 0 ? 30 : 65)
      : (slotStep === 0 ? (metricId ? 50 : 35) : (chartType ? 85 : 65));

  /* ── Handlers ── */
  const handleCreateLayout = () => {
    dispatch({
      type: A.ADD_LAYOUT,
      payload: {
        layoutType,
        cols: isBento ? 4 : cols,
        bentoTemplateId: isBento ? templateId : null,
        slots: Array.from({ length: slotCount }, () => null),
      },
    });
    setDone(true);
  };

  const handleSaveSlot = () => {
    if (!metricId || (!chartType && !isConsolidated)) return;
    // A consolidated table is always a table and carries its settings; any other widget keeps no config of its own
    const data = isConsolidated
      ? { metricId, chartType: 'table', config: { ...consolidated, series: consolidated.series.map(c => ({ label: c.label.trim(), machine_ids: c.machine_ids })) } }
      : { metricId, chartType, config: undefined };
    dispatch({ type: A.SET_SLOT, payload: { layoutId: editLayoutId, slotIdx: editSlotIdx, data } });
    onToast?.(`✓ Widget "${selMetric?.label}" agregado`);
    onClose();
  };

  /* ── Labels ── */
  const stepLabel = mode === 'new'
    ? (step === 0 ? 'Paso 1 de 2' : 'Paso 2 de 2')
    : (slotStep === 0 ? 'Paso 1 de 2' : 'Paso 2 de 2');

  const headTitle = mode === 'new'
    ? (step === 0 ? 'Tipo de layout' : isBento ? 'Plantilla Bento' : 'Columnas')
    : (slotStep === 0 ? 'Seleccionar métrica' : isConsolidated ? 'Configurar tabla' : 'Tipo de visualización');

  return (
    <div className={s.overlay} onClick={e => e.target === e.currentTarget && !done && onClose()}>
      <div className={s.dialog}>

        {/* Progress */}
        <div className={s.progress}>
          <div className={s.progressFill} style={{ width: `${progressPct}%` }} />
        </div>

        {/* Metric indicator — shown on step 2 of slot edit */}
        {!done && mode === 'editOneSlot' && slotStep === 1 && (
          <div className={s.metricBar}>
            {selMetric ? (
              <>
                <div className={s.metricBarDot} style={{ background: selMetric.color }} />
                <span className={s.metricBarLabel}>{selMetric.label}</span>
                <span className={s.metricBarChip}>✓ Seleccionada</span>
                <button
                  onClick={() => setSlotStep(0)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#5046e4', fontFamily: 'inherit', fontWeight: 500, padding: '0 2px', marginLeft: 4 }}
                >
                  Cambiar
                </button>
              </>
            ) : (
              <span style={{ fontSize: 12, color: '#9f9f98' }}>Sin métrica seleccionada</span>
            )}
          </div>
        )}

        {/* Header */}
        {!done && (
          <div className={s.head}>
            <div>
              <div className={s.stepLabel}>{stepLabel}</div>
              <div className={s.headTitle}>{headTitle}</div>
            </div>
            <button className={s.closeBtn} onClick={onClose}>✕</button>
          </div>
        )}

        {/* Body */}
        <div className={s.body}>

          {done && (
            <div className={s.success}>
              <div className={s.successIcon}>✓</div>
              <div className={s.successTitle}>¡Bloque creado!</div>
              <div className={s.successDesc}>
                Se creó con {slotCount} espacio{slotCount > 1 ? 's' : ''} vacío{slotCount > 1 ? 's' : ''}. Toca cada uno para agregar tus métricas.
              </div>
              <button className={`${s.btnBase} ${s.btnSm} ${s.btnPrimary}`} onClick={onClose}>
                Empezar a llenar
              </button>
            </div>
          )}

          {/* ── NEW step 0: layout type ── */}
          {!done && mode === 'new' && step === 0 && (
            <>
              <p style={{ fontSize: 13, color: '#6b6b66', lineHeight: 1.6, marginBottom: 14 }}>Elige cómo organizar este bloque.</p>
              <div className={s.typeGrid}>
                {LAYOUT_TYPES.map(lt => (
                  <div key={lt.id} className={`${s.typeOpt} ${layoutType === lt.id ? s.typeOptSel : ''}`} onClick={() => setLayoutType(lt.id)}>
                    <div className={s.typePreview}><LayoutTypeIcon type={lt.id} active={layoutType === lt.id} /></div>
                    <div className={s.typeLabel}>{lt.label}</div>
                    <div className={s.typeDesc}>{lt.desc}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── NEW step 1: columns ── */}
          {!done && mode === 'new' && step === 1 && !isBento && (
            <>
              <p style={{ fontSize: 13, color: '#6b6b66', lineHeight: 1.6, marginBottom: 14 }}>¿Cuántas columnas tendrá este bloque?</p>
              <div className={s.colGrid}>
                {COL_OPTIONS.map(opt => (
                  <div key={opt.cols} className={`${s.colOpt} ${cols === opt.cols ? s.colOptSel : ''}`} onClick={() => setCols(opt.cols)}>
                    <div className={s.colPreview}>{Array.from({ length: opt.cols }).map((_, i) => <div key={i} className={s.colBlock} />)}</div>
                    <div className={s.colOptLabel}>×{opt.cols}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── NEW step 1: bento ── */}
          {!done && mode === 'new' && step === 1 && isBento && (
            <>
              <p style={{ fontSize: 13, color: '#6b6b66', lineHeight: 1.6, marginBottom: 14 }}>Elige la distribución asimétrica.</p>
              <div className={s.bentoGrid}>
                {BENTO_TEMPLATES.map(t => (
                  <div key={t.id} className={`${s.bentoOpt} ${templateId === t.id ? s.bentoOptSel : ''}`} onClick={() => setTemplateId(t.id)}>
                    <div className={s.bentoMini}>{t.slots.map((def, i) => <div key={i} className={s.bentoCell} style={{ gridColumn: `${def.col}/span ${def.colSpan}`, gridRow: `${def.row}/span ${def.rowSpan}` }} />)}</div>
                    <div className={s.bentoOptLabel}>{t.label} mmmm</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── EDIT slot step 0: metric ── */}
          {!done && mode === 'editOneSlot' && slotStep === 0 && (
            <StepMetric
              metricId={metricId}
              onChange={setMetricId}
              usedMetricIds={usedMetricIds}
            />
          )}

          {/* ── EDIT slot step 1: chart ── */}
          {!done && mode === 'editOneSlot' && slotStep === 1 && !isConsolidated && (
            <StepChart chartType={chartType} onChange={setChartType} />
          )}
          {!done && mode === 'editOneSlot' && slotStep === 1 && isConsolidated && (
            <ConsolidatedConfig value={consolidated} onChange={setConsolidated} />
          )}
        </div>

        {/* Footer */}
        {!done && (
          <div className={s.foot}>
            <span className={s.footNote}>
              {mode === 'new' && step === 1 ? `${slotCount} espacio${slotCount > 1 ? 's' : ''} vacíos` : ''}
            </span>
            <div className={s.footBtns}>
              {mode === 'new' && step === 1 && (
                <button className={`${s.btnBase} ${s.btnSm} ${s.btnSecondary}`} onClick={() => setStep(0)}>Atrás</button>
              )}
              {mode === 'editOneSlot' && slotStep === 1 && (
                <button className={`${s.btnBase} ${s.btnSm} ${s.btnSecondary}`} onClick={() => setSlotStep(0)}>Atrás</button>
              )}

              {mode === 'new' && step === 0 && (
                <button className={`${s.btnBase} ${s.btnSm} ${s.btnPrimary}`} onClick={() => setStep(1)}>Continuar →</button>
              )}
              {mode === 'new' && step === 1 && (
                <button className={`${s.btnBase} ${s.btnSm} ${s.btnPrimary}`} onClick={handleCreateLayout}>✓ Crear bloque</button>
              )}
              {mode === 'editOneSlot' && slotStep === 0 && (
                <button className={`${s.btnBase} ${s.btnSm} ${s.btnPrimary}`} onClick={() => setSlotStep(1)} disabled={!metricId}>
                  Siguiente →
                </button>
              )}
              {mode === 'editOneSlot' && slotStep === 1 && (
                <button className={`${s.btnBase} ${s.btnSm} ${s.btnPrimary}`} onClick={handleSaveSlot} disabled={isConsolidated ? validateConsolidatedConfig(consolidated).length > 0 || consolidated.series.length === 0 : !chartType}>
                  ✓ Agregar widget
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}